import { db, type Point, type Product } from "./db";

// Общие правила, одинаковые для всех экранов.

export const NORMS: Record<string, number> = { A: 30, B: 60, C: 90 };

export const typeName = (t: string) =>
  t === "pharmacy" ? "Аптека"
  : t === "podolog" ? "Подолог"
  : t === "cosmetolog" ? "Косметолог"
  : "Стоматология";

/** Государственную поликлинику и частную клинику надо различать с одного взгляда:
 *  подход к ним разный, и в списке из полутора сотен они иначе сливаются. */
export function typeIcon(p: { type: string; ownership?: string | null }): string {
  if (p.type === "pharmacy") return "💊";
  if (p.type === "podolog") return "🦶";
  if (p.type === "cosmetolog") return "💆";
  return p.ownership === "state" ? "🏥" : "🦷";
}

export function ownershipName(o?: string | null): string | null {
  if (o === "state") return "государственная";
  if (o === "private") return "частная";
  return null;
}

export function daysAgo(iso?: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400_000);
}

export function daysText(d: number | null): string {
  if (d === null) return "не были ни разу";
  if (d === 0) return "сегодня";
  if (d === 1) return "1 день";
  const last = d % 10, hun = d % 100;
  if (last === 1 && hun !== 11) return `${d} день`;
  if (last >= 2 && last <= 4 && (hun < 12 || hun > 14)) return `${d} дня`;
  return `${d} дней`;
}

export function dayClass(p: Point, d: number | null): "g" | "w" | "c" {
  if (d === null) return "c";
  const n = NORMS[p.category] ?? 60;
  return d <= n * 0.6 ? "g" : d <= n ? "w" : "c";
}

/** Ни разу не посещённые — в самое начало списка. */
export const overdue = (p: Point, d: number | null) =>
  d === null ? 9999 : d / (NORMS[p.category] ?? 60);

export const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });

export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

/**
 * Действующая матрица точки по бренду.
 * Своя матрица точки важнее матрицы контрагента — но в пределах бренда.
 * Поэтому сначала отбираем позиции бренда, а уже потом решаем, чья матрица:
 * наоборот перечень Medipharma у точки затирал бы перечень Lacalut от контрагента.
 */
export async function matrixFor(point: Point, brandId: string): Promise<Product[]> {
  const ofBrand = async (rows: { productId: string }[]) =>
    (await db.products.bulkGet(rows.map((r) => r.productId)))
      .filter((p): p is Product => Boolean(p) && p!.brandId === brandId && p!.active);

  let products = await ofBrand(await db.matrix.where({ scope: "point", scopeId: point.id }).toArray());
  if (!products.length && point.contractorId) {
    products = await ofBrand(
      await db.matrix.where({ scope: "contractor", scopeId: point.contractorId }).toArray()
    );
  }
  return products.sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Чья точка. База общая: в ней стоматологии Lacalut и аптеки Medipharma вперемешку,
 * и без этого правила каждый сотрудник листал бы чужой список.
 *
 * Точка относится к бренду, если офис прислал её в списке этого бренда (brandId)
 * или у неё есть свой перечень этого бренда. Перечень по контрагенту признаком
 * не считается: Lacalut по сетям есть почти в каждой аптеке.
 * own — сотрудник сам её завёл или сам там был; общая аптека видна обоим.
 */
export async function pointOwnership(
  userId: string
): Promise<(p: Point) => { brands: string[]; own: boolean }> {
  const brandOf = new Map((await db.products.toArray()).map((p) => [p.id, p.brandId]));
  const listed = new Map<string, Set<string>>();
  for (const r of await db.matrix.toArray()) {
    if (r.scope !== "point" || r.deletedAt) continue;
    const b = brandOf.get(r.productId);
    if (b) listed.set(r.scopeId, (listed.get(r.scopeId) ?? new Set()).add(b));
  }
  // Визит без автора записан на этом устройстве и ещё не уехал — он точно свой.
  const visited = new Set<string>();
  for (const v of await db.visits.toArray()) {
    if (!v.deletedAt && (!v.userId || v.userId === userId)) visited.add(v.pointId);
  }
  return (p) => {
    const brands = new Set(listed.get(p.id));
    if (p.brandId) brands.add(p.brandId);
    return { brands: [...brands], own: p.createdById === userId || visited.has(p.id) };
  };
}

/** Группа позиции — чтобы длинный чек-лист не был сплошной простынёй. */
export function groupOf(p: Product): string {
  // Линейку офис указывает в файле сам (у Medipharma — «Подбренд»). Это надёжнее
  // любых догадок по названию; догадки ниже остались для Lacalut, где её нет.
  if (p.line) return p.line;
  const n = p.name.toLowerCase();
  if (n.includes("фиксац") || n.includes("fix")) return "Фиксация протезов";
  if (n.includes("нить") || n.includes("межзубн")) return "Нити и межзубные щётки";
  if (n.includes("щетка") || n.includes("щётка")) return "Зубные щётки";
  if (n.includes("опол")) return "Ополаскиватели";
  if (n.includes("детск") || n.includes("kids") || n.includes("junior") ||
      n.includes("baby") || n.includes("teens")) return "Детская линейка";
  return "Зубные пасты";
}

/** Остаток раздатки на руках — сумма движений, а не отдельное поле. */
export async function stockLeft(userId: string, productId: string): Promise<number> {
  const moves = await db.stockMoves.where("productId").equals(productId).toArray();
  return moves.filter((m) => m.userId === userId && !m.deletedAt)
    .reduce((s, m) => s + m.qty, 0);
}

export async function allStock(userId: string): Promise<Map<string, number>> {
  const moves = await db.stockMoves.toArray();
  const map = new Map<string, number>();
  for (const m of moves) {
    if (m.userId !== userId || m.deletedAt) continue;
    map.set(m.productId, (map.get(m.productId) ?? 0) + m.qty);
  }
  return map;
}

/** Геометка. Не блокирует сохранение: нет сигнала — визит всё равно записывается. */
export function getPosition(timeoutMs = 12_000): Promise<GeolocationPosition | null> {
  if (!navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 }
    );
  });
}

/**
 * Сжатие снимка прямо на планшете: с камеры прилетает 4–8 МБ, после сжатия
 * около 300 КБ. Это разница между «уехало за секунду» и «висит в очереди».
 */
export async function compressImage(file: File, maxSide = 1600, quality = 0.82): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  return new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b ?? file), "image/jpeg", quality)
  );
}
